import React, { useEffect, useState } from 'react';
import {
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  UserPlusIcon,
  UserMinusIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import adminService from '../../services/admin.service';
import { AdminRevenue as AdminRevenueType } from '../../types/admin.types';

const TIER_LABELS: Record<string, string> = {
  starter: 'Starter',
  growth: 'Growth',
};

const AdminRevenue: React.FC = () => {
  const [data, setData] = useState<AdminRevenueType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const revenue = await adminService.getRevenue();
        setData(revenue);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-white rounded-lg shadow" />
          ))}
        </div>
        <div className="h-80 bg-white rounded-lg shadow" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <p className="text-sm text-red-700">Failed to load revenue data</p>
      </div>
    );
  }

  const netSubscriptions = data.new_subscriptions_30d - data.churned_subscriptions_30d;

  const kpiCards = [
    {
      name: 'Monthly Recurring Revenue',
      value: `$${parseFloat(data.mrr).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      subtext: `${data.total_paid_subscriptions} paid subscriptions`,
      icon: CurrencyDollarIcon,
      color: 'text-green-600',
      bg: 'bg-green-50',
    },
    {
      name: 'Overage Revenue (30d)',
      value: `$${parseFloat(data.overage_revenue_30d).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      icon: ArrowTrendingUpIcon,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
    },
    {
      name: 'New Subscriptions (30d)',
      value: data.new_subscriptions_30d.toString(),
      icon: UserPlusIcon,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      name: 'Churned (30d)',
      value: data.churned_subscriptions_30d.toString(),
      subtext: `Net: ${netSubscriptions >= 0 ? '+' : ''}${netSubscriptions}`,
      icon: data.churned_subscriptions_30d > 0 ? UserMinusIcon : ArrowTrendingDownIcon,
      color: data.churned_subscriptions_30d > 0 ? 'text-red-600' : 'text-gray-600',
      bg: data.churned_subscriptions_30d > 0 ? 'bg-red-50' : 'bg-gray-50',
    },
  ];

  // Format trend data for chart
  const chartData = data.mrr_trend.map((point) => ({
    date: new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    mrr: parseFloat(point.mrr),
  }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Revenue & Billing</h1>
        <p className="mt-1 text-sm text-gray-500">Last 30 days financial metrics</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {kpiCards.map((card) => (
          <div key={card.name} className="overflow-hidden rounded-lg bg-white shadow">
            <div className="p-5">
              <div className="flex items-center">
                <div className={`flex-shrink-0 rounded-md ${card.bg} p-3`}>
                  <card.icon className={`h-6 w-6 ${card.color}`} />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="truncate text-sm font-medium text-gray-500">{card.name}</dt>
                    <dd className="flex items-baseline">
                      <p className="text-2xl font-semibold text-gray-900">{card.value}</p>
                      {card.subtext && (
                        <p className="ml-2 text-sm text-gray-500">{card.subtext}</p>
                      )}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* MRR Trend Chart */}
      <div className="rounded-lg bg-white shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">MRR Trend (30 days)</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 12 }}
                tickLine={false}
                tickFormatter={(value) => `$${value}`}
              />
              <Tooltip
                formatter={(value: number) => [
                  `$${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
                  'MRR',
                ]}
              />
              <Line
                type="monotone"
                dataKey="mrr"
                stroke="#6366F1"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Revenue by Tier */}
      <div className="rounded-lg bg-white shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue by Tier</h3>
        <div className="space-y-4">
          {Object.entries(data.mrr_by_tier).map(([tier, revenue]) => {
            const revenueNum = parseFloat(revenue);
            const mrrNum = parseFloat(data.mrr);
            const percent = mrrNum > 0 ? (revenueNum / mrrNum) * 100 : 0;
            return (
              <div key={tier}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium text-gray-900">
                    {TIER_LABELS[tier] || tier}
                  </span>
                  <span className="text-gray-600">
                    ${revenueNum.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    <span className="ml-2 text-gray-400">({percent.toFixed(0)}%)</span>
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full bg-indigo-500"
                    style={{ width: `${percent}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminRevenue;
